import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CmpBootomnavCoach } from '../anima/Navigation Component/src/components/CmpBootomnavCoach/CmpBootomnavCoach';

// Import the persistent navigation styles
import './PersistentNavigation.css';

interface PersistentNavigationProps {
  className?: string;
}

export const PersistentNavigation: React.FC<PersistentNavigationProps> = ({ 
  className = '' 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);

  // 📱 iOS KEYBOARD FIX: Keep navigation pinned to physical bottom
  // Strategy: Force navigation to ignore keyboard by using layout viewport positioning
  useEffect(() => {
    // Only run on mobile devices
    if (!window.visualViewport || window.innerWidth > 768) {
      return;
    }

    const handleViewportChange = () => {
      if (!navRef.current || !window.visualViewport) return;

      const viewport = window.visualViewport;
      const layoutViewportHeight = window.innerHeight;
      const visualViewportHeight = viewport.height;
      
      // Calculate keyboard height (difference between layout and visual viewport)
      const keyboardHeight = layoutViewportHeight - visualViewportHeight;
      
      // 🎯 KEY INSIGHT: When keyboard opens (keyboardHeight > 0), we need to OFFSET the nav
      // to keep it at the physical bottom of the screen, not the visual viewport bottom
      if (keyboardHeight > 30) { // 30px threshold to avoid false positives
        // Keyboard is open - offset nav DOWN to physical bottom
        // The CSS has bottom: 0, which puts it at visual viewport bottom
        // We need to push it down by keyboardHeight to reach physical bottom
        navRef.current.style.transform = `translateX(-50%) translateY(${keyboardHeight}px)`;
        console.log(`📱 KEYBOARD OPEN: Offsetting nav by ${keyboardHeight}px to stay at physical bottom`);
      } else {
        // Keyboard is closed - reset to normal centered position
        navRef.current.style.transform = 'translateX(-50%) translateY(0px)';
        console.log(`📱 KEYBOARD CLOSED: Nav reset to bottom`);
      }
    };

    // Listen for viewport changes (keyboard open/close)
    window.visualViewport.addEventListener('resize', handleViewportChange);
    window.visualViewport.addEventListener('scroll', handleViewportChange);
    
    // Initial check
    handleViewportChange();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
        window.visualViewport.removeEventListener('scroll', handleViewportChange);
      }
    };
  }, []);

  // Determine active tab based on current route
  const getActiveTab = (): string => {
    const path = location.pathname;
    
    if (path === '/chat' || path === '/') {
      return 'cedar';
    } else if (path === '/mypath' || path.startsWith('/mypath') || 
               path.startsWith('/goal/') || path.startsWith('/plan/') || 
               path.startsWith('/rest-day/') || path === '/plans-review') {
      return 'mypath';
    } else if (path === '/insights' || path.startsWith('/insights')) {
      return 'insights';
    } else if (path === '/settings' || path.startsWith('/settings')) {
      return 'settings';
    }
    // HEALTH SECTION TEMPORARILY REMOVED FOR MVP
    // } else if (path === '/health' || path.startsWith('/health')) {
    //   return 'health';
    
    // Default to cedar if no match
    return 'cedar';
  };

  // Check if we're currently in the /mypath area (including all sub-routes)
  const isInMyPathArea = (): boolean => {
    const path = location.pathname;
    return path === '/mypath' || 
           path.startsWith('/mypath') || 
           path.startsWith('/goal/') || 
           path.startsWith('/plan/') || 
           path.startsWith('/rest-day/') || 
           path === '/plans-review';
  };

  // Handle navigation clicks
  const handleNavigation = (tab: string) => {
    switch (tab) {
      case 'cedar':
        navigate('/chat');
        break;
      case 'my-path':
      case 'mypath':
        // 🏠 HOME BUTTON PATTERN (Jan 22, 2026): Always reset to Today when Plan tab clicked
        // User requirement: "Today is the home state of the plan section. When a user taps the plan 
        // button on the main nav when they are ANYWHERE in the /myplan area I want it to bring them 
        // back here to the today."
        // This works from: goal detail, plan detail, workout detail, browse view, modals, etc.
        if (isInMyPathArea()) {
          // Already in /mypath area - navigate to /mypath with reset state
          navigate('/mypath', { state: { resetToToday: true } });
        } else {
          // Coming from outside /mypath area - just navigate normally
          navigate('/mypath');
        }
        break;
      case 'momentum':
      case 'insights':
        navigate('/insights');
        break;
      case 'settings':
        navigate('/settings');
        break;
      // HEALTH SECTION TEMPORARILY REMOVED FOR MVP
      // case 'health':
      //   navigate('/health');
      //   break;
      default:
        navigate('/chat');
        break;
    }
  };

  const activeTab = getActiveTab();

  return (
    <div 
      ref={navRef}
      className={`persistent-navigation ${className}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <CmpBootomnavCoach 
        activeTab={activeTab}
        className="persistent-nav-component"
        onClickTab={handleNavigation as any}
      />
    </div>
  );
};

export default PersistentNavigation;
